<template>
  <div class="flash-news-page">
    <!-- 顶部工具条 -->
    <div class="top-toolbar">
      <div class="title">语音资讯</div>
      <div class="right-actions">
        <div class="switch">
          <span class="switch-label">语音</span>
          <label class="toggle">
            <input type="checkbox" v-model="voiceEnabled" />
            <span class="slider"></span>
          </label>
        </div>
        <button class="btn link" @click="showSettings = true">设置</button>
      </div>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <span>正在加载最新资讯...</span>
    </div>

    <!-- 错误提示 -->
    <div v-if="errorMessages.length" class="error-panel">
      <div class="error-header">
        <span>数据获取问题</span>
        <button class="btn link" @click="errorMessages = []">清除</button>
      </div>
      <div class="error-list">
        <div
          v-for="(error, index) in errorMessages"
          :key="index"
          class="error-item"
        >
          {{ error }}
        </div>
      </div>
    </div>

    <!-- 主内容：聚合快讯 -->
    <div class="panel">
      <div class="panel-header pink">
        <span class="panel-title">聚合快讯</span>
        <div class="panel-actions">
          <button class="btn primary" @click="toggleMarkImportant">
            {{ markImportantMode ? "取消标记" : "标记重要" }}
          </button>
        </div>
      </div>

      <ul class="news-list" v-if="paginatedNews.length">
        <li
          v-for="(news, index) in paginatedNews"
          :key="news.id || index"
          class="news-item"
          :class="{
            important: news.important,
            marked:
              markImportantMode && selectedNews.includes(news.id || index),
          }"
          @click="toggleSelectNews(news.id || index)"
        >
          <div class="time-row">
            <span
              class="dot"
              :style="{ backgroundColor: getSourceColor(news.source) }"
            ></span>
            <span class="time">{{ news.time }}</span>
            <span class="source-tag">{{ news.source }}</span>
          </div>
          <div class="headline">{{ news.title }}</div>
          <!-- 这里使用news.content来显示内容 -->
          <div class="body">{{ news.content }}</div>
          <div class="chips">
            <span class="chip" @click="openOriginal(news.url)">阅读原文</span>
            <span
              v-for="tag in news.tags"
              :key="tag"
              class="chip"
              @click="filterByTag(tag)"
            >
              {{ tag }}
            </span>
          </div>
        </li>
      </ul>

      <div v-else class="empty-state">
        <p v-if="!loading">暂无资讯数据</p>
        <button class="btn primary" @click="fetchNewsData">重新加载</button>
      </div>

      <!-- 分页器 -->
      <div class="pagination" v-if="displayNews.length > 0">
        <button
          class="pagination-btn"
          :disabled="currentPage === 1"
          @click="changePage(currentPage - 1)"
        >
          上一页
        </button>
        <span class="pagination-info">
          第 {{ currentPage }} 页，共 {{ totalPages }} 页
        </span>
        <button
          class="pagination-btn"
          :disabled="currentPage === totalPages"
          @click="changePage(currentPage + 1)"
        >
          下一页
        </button>
      </div>
    </div>

    <!-- 设置模态框 -->
    <div
      v-if="showSettings"
      class="modal-overlay"
      @click="showSettings = false"
    >
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>资讯设置</h3>
          <button class="close-btn" @click="showSettings = false">×</button>
        </div>
        <div class="modal-body">
          <!-- 数据源选择 -->
          <div class="setting-item">
            <label class="setting-label">数据源选择</label>
            <div class="sources-grid">
              <div
                v-for="source in dynamicNewsSources"
                :key="source.id"
                class="source-item"
              >
                <input
                  type="checkbox"
                  :id="source.id"
                  v-model="source.enabled"
                  class="source-checkbox"
                />
                <label :for="source.id" class="source-label">
                  {{ source.name }}
                </label>
              </div>
            </div>
            <div class="select-all-container">
              <button class="select-all-btn" @click="toggleAllDynamicSources">
                {{ allDynamicSourcesSelected ? "取消全选" : "全选" }}
              </button>
            </div>
          </div>

          <!-- 每页显示数量 -->
          <div class="setting-item">
            <label class="setting-label">每页显示数量</label>
            <input
              type="number"
              v-model.number="pageSize"
              min="5"
              max="50"
              class="setting-input"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn primary" @click="saveSettings">保存</button>
          <button class="btn ghost" @click="resetSettings">重置默认</button>
          <button class="btn ghost" @click="showSettings = false">取消</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "FlashNews",
  data() {
    return {
      voiceEnabled: true,
      showSettings: false,
      showAll: true,
      loading: false,
      markImportantMode: false,
      refreshInterval: 30,
      pageSize: 20,
      keywordFilter: "",
      selectedNews: [],
      errorMessages: [],
      allNews: [],
      currentPage: 1,
      refreshTimer: null,
      dynamicNewsSources: [],
      lastNewsCount: 0,
      lastNewsTime: null,
    };
  },
  computed: {
    displayNews() {
      let news = this.allNews;

      const enabledSources = this.dynamicNewsSources
        .filter((source) => source.enabled)
        .map((source) => source.name);

      news = news.filter((item) => enabledSources.includes(item.source));

      if (this.keywordFilter) {
        const keywords = this.keywordFilter
          .split(",")
          .map((k) => k.trim().toLowerCase());
        news = news.filter((item) => {
          const text = (item.title + item.content).toLowerCase();
          return keywords.some((keyword) => text.includes(keyword));
        });
      }

      if (!this.showAll) {
        news = news.filter((item) => item.important);
      }

      return news;
    },
    paginatedNews() {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.displayNews.slice(startIndex, endIndex);
    },
    totalPages() {
      return Math.ceil(this.displayNews.length / this.pageSize);
    },
    allDynamicSourcesSelected() {
      return this.dynamicNewsSources.every((source) => source.enabled);
    },
  },
  watch: {
    displayNews() {
      this.currentPage = 1;
    },
    pageSize() {
      this.currentPage = 1;
    },
    refreshInterval() {
      this.setupAutoRefresh();
    },
  },
  async mounted() {
    await this.loadSettings();
    await this.fetchNewsData();
    this.setupAutoRefresh();
  },
  beforeUnmount() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  },
  methods: {
    // 检查是否是新新闻
    checkIfNewNews(item) {
      if (!this.lastNewsTime) return true; // 第一次加载，所有都是新的

      const getVal = (obj, key) =>
        typeof obj.get === "function" ? obj.get(key) : obj[key];
      const newsTime = getVal(item, "ShowTime");

      if (!newsTime) return false;

      try {
        // 尝试将时间转换为Date对象进行比较
        const newsTimeMs = new Date(newsTime).getTime();
        // 如果新闻时间比上次刷新时间晚，则认为是新新闻
        return newsTimeMs > this.lastNewsTime;
      } catch (error) {
        // 如果转换失败，则不是新新闻
        return false;
      }
    },

    // 将时间转换为毫秒数用于排序
    getTimeMs(timeData) {
      if (!timeData) return Date.now();

      try {
        // 如果是字符串，尝试解析
        if (typeof timeData === "string") {
          return new Date(timeData).getTime();
        }
        // 如果是Date对象
        else if (timeData instanceof Date) {
          return timeData.getTime();
        }
        // 如果是Bmob日期格式
        else if (timeData && timeData.iso) {
          return new Date(timeData.iso).getTime();
        }
        // 其他情况
        else {
          return new Date(String(timeData)).getTime();
        }
      } catch (error) {
        console.warn("时间转换为毫秒失败:", timeData);
        return Date.now();
      }
    },

    async fetchNewsData() {
  if (this.loading) return;
  this.loading = true;
  this.errorMessages = [];
  
  try {
    const Bmob = this.$bmob;
    if (!Bmob) {
      throw new Error("Bmob SDK未初始化");
    }
    
    // 查询TableMsgClient表
    const query = Bmob.Query("TableMsgClient");
    
    // 按ShowTime降序排序
    query.order("-ShowTime");
    
    // 获取数据
    const results = await query.find();
    
    console.log(`从Bmob获取到 ${results.length} 条数据`);
    
    // 动态获取数据源类型
    await this.extractNewsSources(results);
    
    // 转换数据格式
    const getVal = (obj, key) => {
      try {
        if (typeof obj.get === "function") {
          return obj.get(key);
        }
        return obj[key];
      } catch (e) {
        return null;
      }
    };
    
    const newsList = results.map(item => {
      const id = getVal(item, "OnlyID") || item.objectId;
      const source = getVal(item, "NewsType") || "未知来源";
      
      // 获取标题和内容
      const title = getVal(item, "Title") || "";
      const content = getVal(item, "Content") || "";
      const name = getVal(item, "Name") || "";
      
      // 时间处理 - 提取时分秒
      let timeStr = "";
      let fullTimeStr = ""; // 完整的时间字符串
      const rawTime = getVal(item, "ShowTime");
      
      if (rawTime) {
        try {
          // 首先将原始时间转换为标准格式
          let dateObj;
          
          if (typeof rawTime === "string") {
            // 如果是字符串，尝试直接解析
            dateObj = new Date(rawTime);
            fullTimeStr = rawTime;
          } else if (rawTime.iso) {
            // Bmob日期格式
            dateObj = new Date(rawTime.iso);
            fullTimeStr = rawTime.iso;
          } else if (rawTime instanceof Date) {
            // 已经是Date对象
            dateObj = rawTime;
            fullTimeStr = rawTime.toISOString();
          } else {
            // 其他类型，尝试转换为字符串
            fullTimeStr = String(rawTime);
            dateObj = new Date(fullTimeStr);
          }
          
          // 提取时分秒部分
          if (!isNaN(dateObj.getTime())) {
            const hours = dateObj.getHours().toString().padStart(2, '0');
            const minutes = dateObj.getMinutes().toString().padStart(2, '0');
            const seconds = dateObj.getSeconds().toString().padStart(2, '0');
            timeStr = `${hours}:${minutes}:${seconds}`;
          } else {
            // 如果日期解析失败，尝试从字符串中提取时间部分
            const timeMatch = fullTimeStr.match(/(\d{2}:\d{2}:\d{2})/);
            timeStr = timeMatch ? timeMatch[0] : "时间未知";
          }
        } catch (error) {
          console.warn("时间转换失败:", rawTime, error);
          timeStr = "时间未知";
          fullTimeStr = String(rawTime);
        }
      } else {
        timeStr = "时间未知";
        fullTimeStr = "";
      }
      
      const url = getVal(item, "Url") || "#";
      
      // 检查是否需要语音提示（有新数据）
      const isNew = this.checkIfNewNews(item);
      
      return {
        id: id,
        objectId: item.objectId,
        source: source,
        title: title || name || "无标题",
        content: content || title || name || "无内容",
        time: timeStr, // 只显示时分秒
        fullTime: fullTimeStr, // 完整时间字符串，用于排序和其他用途
        timeMs: this.getTimeMs(rawTime),
        important: false,
        tags: this.extractTags(content || title || ""),
        url: url,
        isNew: isNew,
        name: name
      };
    });
    
    // 按时间倒序排序
    newsList.sort((a, b) => b.timeMs - a.timeMs);
    
    this.allNews = newsList;
    this.currentPage = 1;
    
    // 检查是否有新数据并播放语音提示
    this.checkAndPlayVoiceNotification(newsList);
    
    // 更新最后新闻统计
    this.lastNewsCount = results.length;
    this.lastNewsTime = Date.now();
    
    // 调试：查看时间转换结果
    console.log("时间转换示例（前3条）:", newsList.slice(0, 3).map(n => ({ 
      time: n.time, 
      fullTime: n.fullTime,
      timeMs: n.timeMs 
    })));
    
  } catch (error) {
    console.error("获取新闻数据失败:", error);
    this.errorMessages.push("获取新闻数据失败: " + error.message);
    this.allNews = [];
  } finally {
    this.loading = false;
  }
},
    // 在methods中添加这个方法
    logDataStructure(results) {
      if (!results || results.length === 0) return;

      console.log("===== 数据结构分析 =====");
      const sample = results[0];

      // 获取所有可用的字段
      const getVal = (obj, key) =>
        typeof obj.get === "function" ? obj.get(key) : obj[key];

      // 打印对象的所有属性
      console.log("数据对象:", sample);

      // 尝试获取常见的字段
      const possibleFields = [
        "NewsType",
        "Name",
        "Title",
        "Content",
        "content",
        "Message",
        "message",
        "body",
        "Body",
        "Detail",
        "detail",
      ];

      possibleFields.forEach((field) => {
        const value = getVal(sample, field);
        if (value) {
          console.log(`字段 "${field}":`, value);
        }
      });

      // 如果是Bmob对象，可以尝试查看所有字段
      if (typeof sample.get === "function") {
        console.log("Bmob对象字段:");
        try {
          // Bmob对象可能有一个toJSON方法或者attributes属性
          if (sample.toJSON) {
            console.log("toJSON():", sample.toJSON());
          }
          if (sample.attributes) {
            console.log("attributes:", sample.attributes);
          }
        } catch (e) {
          console.log("无法获取完整字段列表:", e);
        }
      }
      console.log("===== 结束分析 =====");
    },

    // 新增方法：从内容中提取标签
    extractTags(content) {
      if (!content) return [];

      // 这里可以根据需要提取关键词或标签
      // 例如：提取股票代码、公司名称等
      const tags = [];

      // 匹配股票代码（如：600858、000001）
      const stockCodes = content.match(/\b[0-9]{6}\b/g);
      if (stockCodes) {
        tags.push(...stockCodes.slice(0, 3)); // 最多取3个股票代码
      }

      // 匹配公司名称（如：银座股份、同花顺）
      // 这里需要根据实际情况调整，可能需要一个公司名称词典

      return [...new Set(tags)]; // 去重
    },

    // 提取数据源类型
    async extractNewsSources(results) {
      try {
        const getVal = (obj, key) =>
          typeof obj.get === "function" ? obj.get(key) : obj[key];

        // 从数据中提取所有唯一的NewsType
        const sourceTypes = new Set();
        results.forEach((item) => {
          const newsType = getVal(item, "NewsType");
          if (newsType) {
            sourceTypes.add(newsType);
          }
        });

        // 颜色数组
        const colors = [
          "#ff4d4f",
          "#409eff",
          "#f59e0b",
          "#13c2c2",
          "#52c41a",
          "#eb2f96",
          "#fa8c16",
          "#722ed1",
        ];

        // 创建动态数据源
        const sourcesArray = Array.from(sourceTypes);
        const newDynamicSources = sourcesArray.map((type, index) => {
          // 检查是否在已有数据源中
          const existingSource = this.dynamicNewsSources.find(
            (s) => s.name === type
          );

          if (existingSource) {
            return { ...existingSource };
          } else {
            return {
              id: `source-${index}`,
              name: type,
              enabled: true,
              color: colors[index % colors.length],
            };
          }
        });

        // 如果这是第一次加载或者数据源有变化，更新数据源
        if (
          this.dynamicNewsSources.length === 0 ||
          JSON.stringify(newDynamicSources.map((s) => s.name).sort()) !==
            JSON.stringify(this.dynamicNewsSources.map((s) => s.name).sort())
        ) {
          // 保留用户的启用状态
          newDynamicSources.forEach((newSource) => {
            const oldSource = this.dynamicNewsSources.find(
              (s) => s.name === newSource.name
            );
            if (oldSource) {
              newSource.enabled = oldSource.enabled;
            }
          });

          this.dynamicNewsSources = newDynamicSources;
        }

        console.log(
          `提取到 ${this.dynamicNewsSources.length} 个数据源:`,
          this.dynamicNewsSources.map((s) => s.name)
        );
      } catch (error) {
        console.error("提取数据源失败:", error);
      }
    },

    // 检查并播放语音提示
    checkAndPlayVoiceNotification(newsList) {
      if (!this.voiceEnabled || newsList.length === 0) return;

      // 统计新新闻数量
      const newNewsCount = newsList.filter((item) => item.isNew).length;

      if (newNewsCount > 0) {
        console.log(`发现 ${newNewsCount} 条新新闻，播放语音提示`);
        this.playVoiceNotification();
      }
    },

    playVoiceNotification() {
      if ("speechSynthesis" in window) {
        const utterance = new SpeechSynthesisUtterance("有新消息");
        utterance.lang = "zh-CN";
        utterance.rate = 1.0;
        utterance.pitch = 1.0;

        speechSynthesis.cancel();
        speechSynthesis.speak(utterance);
      }
    },

    // 切换标记重要模式
    toggleMarkImportant() {
      this.markImportantMode = !this.markImportantMode;
      if (!this.markImportantMode) {
        this.selectedNews = [];
      }
    },

    // 切换选择新闻
    toggleSelectNews(newsId) {
      if (!this.markImportantMode) return;

      const index = this.selectedNews.indexOf(newsId);
      if (index > -1) {
        this.selectedNews.splice(index, 1);
      } else {
        this.selectedNews.push(newsId);
      }
    },

    // 切换页面
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },

    // 获取数据源颜色
    getSourceColor(sourceName) {
      const source = this.dynamicNewsSources.find((s) => s.name === sourceName);
      return source ? source.color : "#ccc";
    },

    // 打开原文链接
    openOriginal(url) {
      if (url && url !== "#") {
        window.open(url, "_blank");
      }
    },

    // 按标签过滤
    filterByTag(tag) {
      if (this.keywordFilter.includes(tag)) return;

      if (this.keywordFilter) {
        this.keywordFilter += `, ${tag}`;
      } else {
        this.keywordFilter = tag;
      }
    },

    // 设置自动刷新
    setupAutoRefresh() {
      // 清理现有定时器
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
      }

      // 检查是否有启用的数据源
      const hasEnabledSources = this.dynamicNewsSources.some(
        (source) => source.enabled
      );

      if (!hasEnabledSources) {
        console.log("没有选中任何数据源，自动刷新已停止");
        return;
      }

      // 设置新的定时器
      this.refreshTimer = setInterval(() => {
        console.log("执行定时刷新...");
        this.fetchNewsData();
      }, this.refreshInterval * 1000);

      console.log(`自动刷新已启动，间隔: ${this.refreshInterval}秒`);
    },

    // 切换全选数据源
    toggleAllDynamicSources() {
      const allSelected = this.allDynamicSourcesSelected;
      this.dynamicNewsSources.forEach((source) => {
        source.enabled = !allSelected;
      });

      // 数据源状态变化后，重新设置自动刷新
      this.setupAutoRefresh();
    },

    // 加载设置
    async loadSettings() {
      const settings = localStorage.getItem("newsSettings");
      if (settings) {
        try {
          const parsed = JSON.parse(settings);
          this.refreshInterval = parsed.refreshInterval || 30;
          this.pageSize = parsed.pageSize || 20;
          this.keywordFilter = parsed.keywordFilter || "";
          this.voiceEnabled = parsed.voiceEnabled !== false;

          if (parsed.sources && parsed.sources.length > 0) {
            // 等待数据源提取完成后，再应用设置
            setTimeout(() => {
              parsed.sources.forEach((savedSource) => {
                const existingSource = this.dynamicNewsSources.find(
                  (s) => s.name === savedSource.name
                );
                if (existingSource) {
                  existingSource.enabled = savedSource.enabled;
                }
              });
            }, 100);
          }
        } catch (e) {
          console.error("加载设置失败:", e);
          this.resetSettings();
        }
      } else {
        this.resetSettings();
      }
    },

    // 保存设置
    saveSettings() {
      const settings = {
        refreshInterval: this.refreshInterval,
        pageSize: this.pageSize,
        keywordFilter: this.keywordFilter,
        voiceEnabled: this.voiceEnabled,
        sources: this.dynamicNewsSources.map((s) => ({
          id: s.id,
          name: s.name,
          enabled: s.enabled,
        })),
      };
      localStorage.setItem("newsSettings", JSON.stringify(settings));
      this.showSettings = false;

      // 重新设置自动刷新
      this.setupAutoRefresh();
    },

    // 重置设置
    resetSettings() {
      this.refreshInterval = 30;
      this.pageSize = 20;
      this.keywordFilter = "";
      this.voiceEnabled = true;
      this.dynamicNewsSources.forEach((source) => {
        source.enabled = true;
      });

      this.saveSettings();
    },
  },
};
</script>

<style scoped>
.flash-news-page {
  max-width: 960px;
  margin: 0 auto;
  padding: 16px;
  font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica,
    Arial, "Microsoft YaHei", sans-serif;
}

.top-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.top-toolbar .title {
  font-size: 18px;
  font-weight: 600;
}

.right-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.switch {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 13px;
}

.toggle {
  position: relative;
  width: 44px;
  height: 24px;
  display: inline-block;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle .slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #dcdfe6;
  border-radius: 12px;
  transition: background 0.2s ease;
}

.toggle .slider::before {
  content: "";
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease;
}

.toggle input:checked + .slider {
  background: #409eff;
}

.toggle input:checked + .slider::before {
  transform: translateX(20px);
}

.btn {
  height: 28px;
  padding: 0 12px;
  font-size: 13px;
  border: 1px solid #dcdfe6;
  border-radius: 14px;
  background: #fff;
  color: #606266;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:hover {
  background: #f7f7f7;
}

.btn.link {
  border: none;
  color: #409eff;
  background: transparent;
}

.btn.primary {
  color: #fff;
  background: #ff4d4f;
  border-color: #ff4d4f;
}

.btn.ghost {
  background: #fff;
}

.panel {
  background: #fff;
  border: 1px solid #ebedf0;
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.panel-header.pink {
  background: #fff1f0;
}

.panel-header.blue {
  background: #f0f7ff;
}

.panel-title {
  font-weight: 600;
}

.error-panel {
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 8px;
  margin-bottom: 16px;
  overflow: hidden;
}

.error-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #fff2f0;
  border-bottom: 1px solid #ffccc7;
  font-weight: 600;
  color: #a8071a;
}

.error-list {
  padding: 8px 12px;
}

.error-item {
  font-size: 13px;
  color: #a8071a;
  margin-bottom: 4px;
}

/* 新闻列表样式优化 */
.news-list {
  list-style: none;
  margin: 0;
  padding: 8px 12px 12px;
}

.news-item + .news-item {
  border-top: 1px dashed #eee;
}

.time-row {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #888;
  font-size: 13px;
  margin: 8px 0;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.source-tag {
  font-size: 12px;
  background: #f0f0f0;
  padding: 1px 6px;
  border-radius: 4px;
}

/* 修复：确保时间显示正确，保留所有空格 */
.time {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
    monospace;
  white-space: pre; /* 保留所有空格和换行 */
}

.headline {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  line-height: 1.6;
  margin-bottom: 4px;
}

.body {
  font-size: 14px;
  color: #666;
  line-height: 1.7;
}

.chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 8px 0 12px;
}

.chip {
  font-size: 12px;
  color: #409eff;
  background: #ecf5ff;
  border: 1px solid #d7eaff;
  border-radius: 12px;
  padding: 2px 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.chip:hover {
  background: #d7eaff;
}

.news-item.important .headline {
  color: #ff4d4f;
  font-weight: 700;
}

.news-item.marked {
  background-color: #fff7e6;
  border-left: 3px solid #fa8c16;
}

/* 骨架屏样式优化 */
.skeleton-container {
  padding: 12px;
}

.skeleton-item {
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: white;
}

.skeleton-time {
  height: 14px;
  width: 80px;
  background: #f0f0f0;
  border-radius: 4px;
  margin-bottom: 12px;
  animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-title {
  height: 18px;
  width: 70%;
  background: #f0f0f0;
  border-radius: 4px;
  margin-bottom: 8px;
  animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-content {
  height: 14px;
  width: 90%;
  background: #f0f0f0;
  border-radius: 4px;
  margin-bottom: 12px;
  animation: pulse 1.5s ease-in-out infinite;
}

.skeleton-tags {
  height: 12px;
  width: 120px;
  background: #f0f0f0;
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  color: #666;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #409eff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.empty-state {
  padding: 40px 20px;
  text-align: center;
  color: #999;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  border-top: 1px solid #f0f0f0;
  gap: 16px;
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #606266;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: #f7f7f7;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 14px;
  color: #666;
}

/* 设置模态框样式优化 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #999;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #666;
}

.modal-body {
  padding: 20px;
}

.setting-item {
  margin-bottom: 20px;
}

.setting-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.setting-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.setting-input:focus {
  outline: none;
  border-color: #409eff;
}

/* 数据源选择样式优化 */
.sources-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;
}

.source-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.source-checkbox {
  width: 16px;
  height: 16px;
  margin: 0;
}

.source-label {
  font-size: 14px;
  color: #606266;
  cursor: pointer;
}

.select-all-container {
  display: flex;
  justify-content: flex-start;
}

.select-all-btn {
  background: none;
  border: none;
  color: #409eff;
  font-size: 14px;
  cursor: pointer;
  padding: 4px 0;
}

.select-all-btn:hover {
  color: #66b1ff;
}

.modal-footer {
  padding: 12px 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* 响应式调整优化 */
@media (max-width: 640px) {
  .flash-news-page {
    padding: 12px;
  }

  .panel {
    border-radius: 6px;
  }

  .panel-header {
    padding: 8px 10px;
  }

  .pagination {
    flex-direction: column;
    gap: 8px;
  }

  .modal-footer {
    flex-direction: column;
  }

  .sources-grid {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .source-label {
    font-size: 13px;
  }

  .modal-content {
    width: 95%;
    margin: 20px;
  }

  /* 移动端新闻项优化 */
  .news-item {
    padding: 10px 8px;
  }

  .headline {
    font-size: 14px;
  }

  .body {
    font-size: 13px;
  }
}

/* 只在非常小的屏幕上变为单列 */
@media (max-width: 360px) {
  .sources-grid {
    grid-template-columns: 1fr;
  }
}
</style>